import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from "@ngrx/store";
import { catchError, concatMap, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";


import { AuthActions } from "../actions/auth.actions";
import { DealerActions } from "../actions/dealer.actions";
import { FilesActions } from "../actions/files.actions";
import { MessageActions } from "../actions/message.actions";
import { Contract } from "src/app/public/account/models/contract.model";
import { Dealer } from "src/app/public/account/models/dealer.model";
import { ReviewReply } from "src/app/public/account/models/review-reply.model";
import { Review } from "src/app/public/account/models/review.model";
import { DealerService } from "src/app/public/account/services/dealer.service";
import { Alert } from "src/app/public/car/models/alert.model";
import { Car } from "src/app/public/car/models/car.model";
import { CarStatus } from "src/app/public/car/constants/car-basic-data";
import { StringData } from "src/app/public/common/models/string-data.model";
import { KeepOffer } from "src/app/public/keep/models/keep-offer.model";

@Injectable()
export class DealerApiEffects{

    constructor(
        private dealerService: DealerService,
        private actions$: Actions,
        private store: Store
    ){}

    // dealer profile

    fetchDealerProfile$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(AuthActions.authSuccess, DealerActions.fetchMyDealerProfileSubmit),
            concatMap( (action) => {  
                return this.dealerService
                    .getDealerDetails()
                    .pipe( 
                        map( (dealer: Dealer) => { 
                            return DealerActions.fetchMyDealerProfileSuccess ( { dealer: Object.assign(new Dealer(), dealer) } );
                            })
                        )
            })
        )
    });


    updateDealerProfile$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.updateDealerProfileSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateDealerProfile (action.dlUpdates)
                    .pipe( 
                        map( (dealer: Dealer) => { 
                            return DealerActions.updateDealerProfileSuccess ( {dealer: dealer} );
                            })
                        )
            })
        )
    });

    fetchProviderPublicDetails$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchProviderPublicDetailsSubmit),
            mergeMap( (action) => {
                return this.dealerService
                    .getProviderPublicDetailsById(action.providerId)
                    .pipe( 
                        map( (provider: Dealer) => { 
                            
                            return DealerActions.fetchProviderPublicDetailsSuccess ({provider: provider});
                            
                        }),
                        catchError( errorResponse => {
                            console.log("Error fetching provider public details = "+JSON.stringify(errorResponse))
                           
                            return [
                                DealerActions.dealerFetchError( {errorMessage: "Error fetching provider public details =  "+errorResponse} )
                                 ]
                        })
                        )
            })
        )
    });

    toggleFavoriteProvider$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.toggleFavoriteProviderSubmit),
            mergeMap( (action) => {
                return this.dealerService
                    .toggleFavoriteProvider(action.providerId, action.makeFav)
                    .pipe( 
                        map( (result: boolean) => { 
                            
                            return DealerActions.toggleFavoriteProviderSuccess ({providerId: action.providerId, makeFav: action.makeFav});
                            
                        }),
                        catchError( errorResponse => {
                            console.log("Error updating provider fav = "+JSON.stringify(errorResponse))
                           
                            return [
                                DealerActions.dealerFetchError( {errorMessage: "Error updating provider fav =  "+errorResponse} )
                                 ]
                        })
                        )
            })
        )
    });

    fetchMyProviders$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchMyProvidersSubmit),
            mergeMap( (action) => {
                return this.dealerService
                    .getMyProviders()
                    .pipe( 
                        map( (providers: Dealer[]) => { 
                            
                            return DealerActions.fetchMyProvidersSuccess ({providers: providers});
                            
                        }),
                        catchError( errorResponse => {
                            console.log("Error fetching my providers = "+JSON.stringify(errorResponse))
                           
                            return [
                                DealerActions.dealerFetchError( {errorMessage: "Error fetching my providers =  "+errorResponse} )
                                 ]
                        })
                        )
            })
        )
    });

    fetchMyOwners$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchMyOwnersSubmit),
            mergeMap( (action) => {
                return this.dealerService
                    .getMyOwners()
                    .pipe( 
                        map( (owners: Dealer[]) => { 
                            
                            return DealerActions.fetchMyOwnersSuccess ({owners: owners});
                            
                        }),
                        catchError( errorResponse => {
                            console.log("Error fetching my owners = "+JSON.stringify(errorResponse))
                           
                            return [
                                DealerActions.dealerFetchError( {errorMessage: "Error fetching my owners =  "+errorResponse} )
                                 ]
                        })
                        )
            })
        )
    });

    fetchMyCarsforOwner$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchOwnerParkSubmit),
            switchMap( (action) => {
                return this.dealerService
                    .getMyCarsForOwner(action.carCriteria)
                    .pipe( 
                        switchMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchParkSuccess ( {cars} )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames!.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    fetchMyCarsforProvider$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchProviderParkSubmit),
            switchMap( (action) => {
                return this.dealerService
                    .getMyCarsForProvider()
                    .pipe( 
                        switchMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchParkSuccess ( {cars} )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames!.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    
    fetchCustomerCars$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchCustomerCarsSubmit),
            switchMap( (action) => {
                return this.dealerService
                    .getCustomerCars(action.businessLine, action.favoritesOnly)
                    .pipe( 
                        switchMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchCustomerCarsSuccess ( { businessLine: action.businessLine, cars: cars } )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames!.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    fetchProviderCars$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchProviderCarsSubmit),
            switchMap( (action) => {
                return this.dealerService
                    .getProviderCars(action.businessLine)
                    .pipe( 
                        switchMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchProviderCarsSuccess ( { businessLine: action.businessLine, cars: cars } )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames!.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    fetchAdminCarBasket$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchAdminCarBasketSubmit),
            switchMap( (action) => {
                return this.dealerService
                    .getAdminCarBasket(action.businessLine)
                    .pipe( 
                        switchMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchAdminCarBasketSuccess ( { businessLine: action.businessLine, cars: cars } )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames!.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });
/*
    fetchMyFavoriteCarsOld$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchFavoriteCarsSubmit),
            withLatestFrom(this.store.select(selectDealerFavorites)),
            switchMap( ([action, myFavoriteCarsIds]) => {
                console.log("car ids to fetch = "+myFavoriteCarsIds);
                return this.dealerService
                    .getMyFavCars(myFavoriteCarsIds)
                    .pipe( 
                        mergeMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchFavoriteCarsSuccess ( {cars} )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                                    
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    fetchMyFavoriteCars$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchFavoriteCarsSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .getMyFavCars(action.favorites)
                    .pipe( 
                        mergeMap( (cars: Car[]) => { 
                            let res: Action[] = [DealerActions.fetchFavoriteCarsSuccess ( {cars} )];
                            cars.forEach( car => {
                                
                                if(car.imagesFilenames != undefined && car.imagesFilenames.length > 0){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: car.carId, filename: car.imagesFilenames[0] } )) );
                                }
                                    
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });


    fetchMyFavoriteCarBriefs$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchFavoriteCarBriefsSubmit),
            withLatestFrom(this.store.select(selectDealerFavorites)),
            switchMap( ([action, myFavoriteCarsIds]) => {
                //console.log("car ids to fetch = "+myFavoriteCarsIds);

                let crt = Object.assign(new CarCriteria(), {'ids': myFavoriteCarsIds});

                return this.searchService
                    .getCarBriefsByCriteria(crt)
                    .pipe( 
                        mergeMap( (carBriefs: CarBrief[]) => { 
                            let res: Action[] = [DealerActions.fetchFavoriteCarBriefsSuccess ( {carBriefs} )];
                            carBriefs.forEach( carBrief => {
                                
                                if(carBrief.imageFilename !== ""){
                                    res.push(FilesActions.fetchCarImageSubmit(( {carId: carBrief.id, filename: carBrief.imageFilename } )) );
                                }
                                    
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });
    */
    toggleFavorite$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.toggleFavoriteSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .toggleFavorite(action.objectId, action.objectClass, action.makeFav, action.businessLine)
                    .pipe( 
                        map( (res: StringData) => { 
                            return DealerActions.toggleFavoriteSuccess ( { objectId: res.data, objectClass: action.objectClass, makeFav: action.makeFav, businessLine: action.businessLine } );
                            })
                        )
            })
        )
    });

    toggleBusinessLineStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.toggleCarBusinessLineStatusSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .toggleCarBusinessLineStatus(action.carId, action.status, action.businessLine)
                    .pipe( 
                        map( (res: StringData) => { 
                            //console.log("status = "+JSON.parse(res.data.toLowerCase()) + res.data);
                            return DealerActions.toggleCarBusinessLineStatusSuccess ( {carId: action.carId, status: JSON.parse(res.data.toLowerCase()), businessLine: action.businessLine} );
                            })
                        )
            })
        )
    });

    //providers

    updateCarProvider$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.updateCarProviderSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateCarProvider (action.carId, action.providerId)
                    .pipe( 
                        map( (result: Car) => { 
                            if(result.carId !== undefined)
                                return DealerActions.updateCarSuccess( {car: result } );
                            else 
                                return DealerActions.dealerFetchError({errorMessage: "error while updating car provider"})
                            })
                        )
            })
        )
    });

    updateCarProviderActiveStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.updateCarProviderActiveSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateCarProviderActiveStatus (action.carId, action.providerActive)
                    .pipe( 
                        map( (result: Car) => { 
                            if(result.carId !== undefined)
                                return DealerActions.updateCarSuccess( {car: result } );
                            else 
                                return DealerActions.dealerFetchError({errorMessage: "error while updating provider active"})
                            })
                        )
            })
        )
    });

    // alerts

    addNewAlert$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.addNewAlertSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .addNewAlert (action.criteria)
                    .pipe( 
                        map( (alert: Alert) => { 
                            //if(carCriteria !== null)
                                return DealerActions.addNewAlertSuccess ( {alert: alert} );
                            })
                        )
            })
        )
    });

    fetchMyAlerts$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.fetchMyAlertsSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .fetchMyAlerts (action.businessLine)
                    .pipe( 
                        map( (alerts: Alert[]) => { 
                            //if(carCriteria !== null)
                                return DealerActions.fetchMyAlertsSuccess ( { alerts: alerts} );
                            })
                        )
            })
        )
    });

    updateAlertCriteria$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.alertUpdateCriteriaSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateAlertCriteria (action.alertId, action.carCriteria)
                    .pipe( 
                        map( (alert: Alert) => { 
                            
                                return DealerActions.alertUpdateSuccess ( { alert: alert } );
                            })
                        )
            })
        )
    });

    updateAlertStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.alertUpdateStatusSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateAlertStatus (action.businessLine, action.alertId, action.status)
                    .pipe( 
                        map( (alert: Alert) => { 
                            
                                return DealerActions.alertUpdateSuccess ( { alert: alert } );
                            })
                        )
            })
        )
    });

    deleteAlert$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.alertDeleteSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .deleteAlert (action.businessLine, action.alertId)
                    .pipe( 
                        map( (index: number) => { 
                            //if(carCriteria !== null)
                                return DealerActions.alertDeleteSuccess ( { businessLine: action.businessLine, alertId: action.alertId } );
                            })
                        )
            })
        )
    });


    // cars

    createNewCar$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.createNewCarSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .createNewCar (action.car)
                    .pipe( 
                        map( (car: Car) => { 
                            return DealerActions.createNewCarSuccess ( {car: car} );
                            })
                        )
            })
        )
    });

    updateCar$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.updateCarSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateCarDetails (action.carUpdates)
                    .pipe( 
                        map( (car: Car) => { 
                            return DealerActions.updateCarSuccess ( {car: car} );
                            })
                        )
            })
        )
    });

    deleteCar$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.deleteCarSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .deleteCar (action.carId)
                    .pipe( 
                        map( (res: number) => { 
                            return DealerActions.deleteCarSuccess( {carId: action.carId} );
                            })
                        )
            })
        )
    });

    
    updateCarStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.updateCarStatusSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateCarStatus(action.carId, action.status)
                    .pipe( 
                        map( (res: CarStatus) => { 
                            return DealerActions.updateCarStatusSuccess ( {carId: action.carId, status: res} );
                            })
                        )
            })
        )
    });

    createNewCarPrice$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.createNewCarPriceSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .createNewPrice (action.carId, action.businessLine, action.price)
                    .pipe( 
                        map( (result: any) => { 
                            return DealerActions.createNewCarPriceSuccess( {carId: action.carId, businessLine: action.businessLine, price: result } );
                            })
                        )
            })
        )
    });



    // provider offers

    createNewProviderOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOfferNewSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .createNewProviderOffer(action.providerOffer)
                    .pipe( 
                        map( (car: Car) => { 
                            return DealerActions.providerOfferUpsertSuccess ( { providerOffer: car, objectSource: action.objectSource } );
                            })
                        )
            })
        )
    });

    updateProviderOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOfferUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateProviderOffer (action.providerOffer)
                    .pipe( 
                        map( (car: Car) => { 
                            return DealerActions.providerOfferUpsertSuccess ( { providerOffer: car, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    deleteProviderOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOfferDeleteSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .deleteProviderOffer (action.providerOfferId, action.businessLine)
                    .pipe( 
                        map( (res: number) => { 
                            return DealerActions.providerOfferDeleteSuccess( { carId: action.carId, providerOfferId: action.providerOfferId, businessLine: action.businessLine, objectSource: action.objectSource } );
                            })
                        )
            })
        )
    });

    
    toggleProviderOfferPostedStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOfferTogglePostedSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateProviderOfferPostedStatus(action.providerOfferId, action.businessLine)
                    .pipe( 
                        map( (result: Car) => { 
                            return DealerActions.providerOfferUpsertSuccess ( { providerOffer: result, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    toggleProviderOfferClosedStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOfferToggleClosedSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateProviderOfferClosedStatus(action.providerOfferId, action.businessLine)
                    .pipe( 
                        map( (result: Car) => { 
                            return DealerActions.providerOfferUpsertSuccess ( { providerOffer: result, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    /*
    fetchMyProviderOffers$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOffersFetchMySubmit),
            concatMap( (action) => {
                return this.dealerService
                    .fetchMyProviderOffers (action.businessLine, action.transactionRole, action.transactionStage)
                    .pipe( 
                        map( (providerOffers: Car[]) => { 
                            //if(carCriteria !== null)
                                return DealerActions.providerOffersFetchMySuccess ( { providerOffers: providerOffers, businessLine: action.businessLine} );
                            })
                        )
            })
        )
    });
    */
    fetchMyProviderOffers$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.providerOffersFetchMySubmit),
            switchMap( (action) => {
                return this.dealerService
                    .fetchMyProviderOffers(action.businessLine, action.transactionRole, action.transactionStages)
                    .pipe( 
                        switchMap( (providerOffers: Car[])  => { 
                            let res: Action[] = [DealerActions.providerOffersFetchMySuccess ( { providerOffers: providerOffers, businessLine: action.businessLine} )];
                            providerOffers.forEach( offer => {
                                
                                if(offer.imagesFilenames != undefined && offer.imagesFilenames!.length > 0){
                                    offer.imagesFilenames.forEach(filename => {
                                        res.push(FilesActions.fetchCarImageSubmit(( {carId: offer.carId, filename: filename } )) );
                                    })
                                }
                                
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });

    createNewProviderOfferPrice$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.createNewCarPriceSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .createNewPrice (action.carId, action.businessLine, action.price)
                    .pipe( 
                        map( (result: any) => { 
                            return DealerActions.createNewCarPriceSuccess( {carId: action.carId, businessLine: action.businessLine, price: result } );
                            })
                        )
            })
        )
    });



    // keep offers

    createNewKeepOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOfferNewSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .createNewKeepOffer(action.keepOffer)
                    .pipe( 
                        map( (offer: KeepOffer) => { 
                            return DealerActions.keepOfferUpsertSuccess ( { keepOffer: offer, objectSource: action.objectSource } );
                            })
                        )
            })
        )
    });

    updateKeepOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOfferUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateKeepOffer (action.keepOffer)
                    .pipe( 
                        map( (offer: KeepOffer) => { 
                            return DealerActions.keepOfferUpsertSuccess ( { keepOffer: offer, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    deleteKeepOffer$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOfferDeleteSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .deleteKeepOffer (action.keepOfferId)
                    .pipe( 
                        map( (res: number) => { 
                            return DealerActions.keepOfferDeleteSuccess( { keepOfferId: action.keepOfferId, objectSource: action.objectSource } );
                            })
                        )
            })
        )
    });

    
    toggleKeepOfferPostedStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOfferTogglePostedSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateKeepOfferPostedStatus(action.keepOfferId)
                    .pipe( 
                        map( (result: KeepOffer) => { 
                            return DealerActions.keepOfferUpsertSuccess ( { keepOffer: result, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    toggleKeepOfferClosedStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOfferToggleClosedSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateKeepOfferClosedStatus(action.keepOfferId)
                    .pipe( 
                        map( (result: KeepOffer) => { 
                            return DealerActions.keepOfferUpsertSuccess ( { keepOffer: result, objectSource: action.objectSource  } );
                            })
                        )
            })
        )
    });

    /*
    fetchMyKeepOffers$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOffersFetchMySubmit),
            concatMap( (action) => {
                return this.dealerService
                    .fetchMyKeepOffers (action.businessLine, action.transactionRole, action.transactionStage)
                    .pipe( 
                        map( (keepOffers: Car[]) => { 
                            //if(carCriteria !== null)
                                return DealerActions.keepOffersFetchMySuccess ( { keepOffers: keepOffers, businessLine: action.businessLine} );
                            })
                        )
            })
        )
    });
    */
    fetchMyKeepOffers$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.keepOffersFetchMySubmit),
            switchMap( (action) => {
                return this.dealerService
                    .fetchMyKeepOffers(action.transactionRole, action.transactionStages)
                    .pipe( 
                        switchMap( (keepOffers: KeepOffer[])  => { 
                            let res: Action[] = [DealerActions.keepOffersFetchMySuccess ( { keepOffers: keepOffers} )];
                            keepOffers.forEach( offer => {
                                
                                if(offer.imagesFilenames != undefined && offer.imagesFilenames!.length > 0){
                                    offer.imagesFilenames.forEach(filename => {
                                        res.push(FilesActions.fetchCarImageSubmit(( {carId: offer.offerId, filename: filename } )) );
                                    })
                                }
                                
                            });
                            
                            return res;
                            
                        })
                        )
            })
        )
    });




    // messages

    sendNewMessage$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(MessageActions.sendNewMessageSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .sendNewMessage (action.message)
                    .pipe( 
                        map( (res: any) => { 
                            return MessageActions.sendNewMessageSuccess( {conversation: res, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    /* 
    replyMessage$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(MessageActions.replyMessageSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .replyMessage (action.reply, action.conversationId)
                    .pipe( 
                        map( (res: Message) => { 
                            return MessageActions.replyMessageSuccess( {message: res, carId: action.carId, conversationId: action.conversationId} );
                            })
                        )
            })
        )
    });

    fetchMessages$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(MessageActions.fetchMessagesSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .fetchSentMessages ()
                    .pipe( 
                        map( (res: any) => { 
                            return MessageActions.fetchMessagesSuccess( {messages: res} );
                            })
                        )
            })
        )
    });

    */
    // reviews

    addNewReview$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.reviewAddSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .addNewReview (action.review)
                    .pipe( 
                        map( (result: Review) => { 
                            return DealerActions.fetchProviderPublicDetailsSubmit( { providerId: result.ratedId } );
                            })
                        )
            })
        )
    });

    updateReviewStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.reviewStatusUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateReviewStatus (action.reviewId, action.status)
                    .pipe( 
                        map( (result: Review) => { 
                            return DealerActions.reviewStatusUpdateSuccess( {review: result } );
                            })
                        )
            })
        )
    });


    replyReview$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.reviewReplySubmit),
            concatMap( (action) => {
                return this.dealerService
                    .replyReview (action.reviewId, action.reply)
                    .pipe( 
                        map( (result: ReviewReply) => { 
                            return DealerActions.reviewReplySuccess( {reviewId: action.reviewId, reply: result} );
                            })
                        )
            })
        )
    });

    updateReviewReplyStatus$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.reviewReplyStatusUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .updateReviewReplyStatus (action.reviewId, action.replyIndex, action.status)
                    .pipe( 
                        map( (result: ReviewReply) => { 
                            return DealerActions.reviewReplyStatusUpdateSuccess( {reviewId: action.reviewId, replyIndex: action.replyIndex, reply: result } );
                            })
                        )
            })
        )
    });

    // contracts

    createNewContract$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractNewSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractAddNew (action.contract)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    updateContract$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractUpdate (action.contract)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    deleteContract$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractDeleteSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractDelete (action.contractId)
                    .pipe( 
                        map( (res: boolean) => { 
                            return res ? DealerActions.contractDeleteSuccess( {contractId: action.contractId, objectSource: action.objectSource} ) : DealerActions.doNothing( );
                            })
                        )
            })
        )
    });

    submitContractModification$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractModificationNewSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractModificationSubmit (action.contractId, action.request)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    updateContractModification$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractModificationUpdateSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractModificationUpdate (action.contractId, action.request)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    withdrawContractModification$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractModificationWithdrawSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractModificationWithdraw (action.contractId, action.modificationId)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    answerContractModification$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractModificationAnwerSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .contractModificationAnswer (action.contractId, action.modificationId, action.accepted)
                    .pipe( 
                        map( (contract: Contract) => { 
                            return contract === null ? DealerActions.doNothing(): DealerActions.contractUpsertSuccess ( {contract: contract, objectSource: action.objectSource} );
                            })
                        )
            })
        )
    });

    fetchAllMyContracts$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.contractFetchAllMySubmit),
            concatMap( () => {
                return this.dealerService
                    .contractGetAllMy ()
                    .pipe( 
                        map( (contracts: Contract[]) => { 
                                return DealerActions.contractFetchAllMySuccess ( {contracts: contracts} );
                            })
                        )
            })
        )
    });


    // Menu

    fetchMenu$ = createEffect( () => {
        return this.actions$.pipe(
            ofType(DealerActions.menuFetchSubmit),
            concatMap( (action) => {
                return this.dealerService
                    .menuGetAllMy (action.codes)
                    .pipe( 
                        map( (menu: { [key: string]: number }) => { 
                                return DealerActions.menuFetchSuccess ( {menu: menu} );
                            })
                        )
            })
        )
    });

}